import { portraitIndexUrl, portraitUrls, type PortraitIndex } from "./portrait-index";
import { characterImagePublicUrl } from "./character-image";

const AT_ONCE = 6;

/** Every address the player needs for a book, given what it says it contains. */
export function bookDownloadList(
  bookId: string,
  chapterIds: readonly string[],
  imageUrls: readonly string[],
): string[] {
  const data = `/book-data/${bookId}`;
  return [
    `${data}/book.json`,
    `${data}/universe.bundle.json`,
    `${data}/loading-phrases.json`,
    `${data}/compendium.json`,
    `${data}/key-items.json`,
    `${data}/character-pages.json`,
    ...chapterIds.map((id) => `${data}/${id}.bundle.json`),
    ...imageUrls,
  ];
}

/** Fetches them a few at a time. The app's worker keeps what passes through. Returns failures. */
export async function fetchAll(
  urls: readonly string[],
  onProgress: (done: number) => void,
  load: (url: string) => Promise<{ ok: boolean }>,
): Promise<number> {
  let next = 0;
  let done = 0;
  let failed = 0;
  const worker = async (): Promise<void> => {
    while (next < urls.length) {
      const url = urls[next]!;
      next += 1;
      const ok = await load(url).then((response) => response.ok, () => false);
      failed += ok ? 0 : 1;
      done += 1;
      onProgress(done);
    }
  };
  await Promise.all(Array.from({ length: AT_ONCE }, worker));
  return failed;
}

export async function downloadBook(
  bookId: string,
  onProgress: (done: number, total: number) => void,
): Promise<number> {
  const base = import.meta.env.VITE_PORTRAIT_BASE;
  const summary = await (await fetch(`/book-data/${bookId}/book.json`)).json() as {
    chapters: { id: string }[];
  };
  const index = await (await fetch(portraitIndexUrl(bookId, base))).json() as PortraitIndex;
  const urls = portraitUrls(index, base);
  const images = index.files.map((file) => urls?.[file] ?? characterImagePublicUrl(bookId, file));
  const list = bookDownloadList(bookId, summary.chapters.map((chapter) => chapter.id), images);
  return fetchAll(list, (done) => onProgress(done, list.length), (url) => fetch(url));
}
